import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.companyName || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitStatus('success');
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        message: ''
      });
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 px-6 bg-[#2B5797]">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-6xl font-bold text-white text-center mb-6">
          Book a Free Strategy Call
        </h2>
        <p className="text-xl text-gray-200 text-center mb-12">
          We'll discuss your business, current lead flow, and how we can help you win more jobs.
        </p>

        {/* Founding Client Offer Banner */}
        <div className="mb-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-center">
          <p className="text-sm font-semibold text-white/90 mb-2 tracking-wide">
            Founding Client Offer
          </p>
          <p className="text-base text-white/80 leading-relaxed">
            We're currently onboarding a small number of extension and loft conversion companies for personalised setup and preferential pricing.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-10">
          {/* Row 1: Full Name & Company Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full Name"
                required
                className="w-full h-14 px-5 border border-gray-300 rounded-lg text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#2B5797] focus:ring-2 focus:ring-[#2B5797]/20 transition-all"
              />
            </div>
            <div>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Company Name"
                required
                className="w-full h-14 px-5 border border-gray-300 rounded-lg text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#2B5797] focus:ring-2 focus:ring-[#2B5797]/20 transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div className="mb-5">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              required
              className="w-full h-14 px-5 border border-gray-300 rounded-lg text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#2B5797] focus:ring-2 focus:ring-[#2B5797]/20 transition-all"
            />
          </div>

          {/* Phone */}
          <div className="mb-5">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              required
              className="w-full h-14 px-5 border border-gray-300 rounded-lg text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#2B5797] focus:ring-2 focus:ring-[#2B5797]/20 transition-all"
            />
          </div>

          {/* Message */}
          <div className="mb-8">
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your business (optional)"
              rows={4}
              maxLength={500}
              className="w-full px-5 py-4 border border-gray-300 rounded-lg text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#2B5797] focus:ring-2 focus:ring-[#2B5797]/20 transition-all resize-none"
            />
            <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
          </div>

          {/* Privacy Reassurance */}
          <p className="text-sm text-gray-600 text-center mb-6">
            Your details are safe with us. We'll never spam you or share your information.
          </p>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-16 bg-[#1E3A5F] text-white text-lg font-bold rounded-lg hover:bg-[#2B5797] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap cursor-pointer hover:scale-105"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Sending...
              </>
            ) : (
              <>
                Book My Free Call
                <i className="ri-arrow-right-line ml-2"></i>
              </>
            )}
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg text-[#10B981] text-center">
              <i className="ri-checkbox-circle-line text-xl mr-2"></i>
              Thank you! We'll be in touch shortly to schedule your strategy call.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center">
              <i className="ri-error-warning-line text-xl mr-2"></i>
              Please fill in all required fields.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
